import React, { useEffect, useRef, useState } from "react";
import type {
  Office,
  OfficeListItem,
  User,
  Config,
  ConfigVersion,
  Reference,
  FileRecord,
  Permission,
} from "../shared/contracts.ts";
import { createRoot } from "react-dom/client";
import tokens from "../docs/design/TOKENS.json" with { type: "json" };
import "./styles.css";
import {
  api,
  date,
  money,
  bytes,
  fileUrl,
  Icon,
  Button,
  Banner,
  Badge,
  Empty,
  Field,
  Modal,
  tabKeys,
  permissionLabels,
} from "./ui.tsx";
import type { PublicIdentity, Notice, Integration } from "./ui.tsx";
import { setCSRF } from "./ui.tsx";
import { OfficeList, OfficeEditor } from "./offices.tsx";
import { Library, Users, Configuration } from "./admin.tsx";
import { VisitsOverview, VisitsOverviewAdmin } from "./visits-overview.tsx";
import { Maps } from "./maps.tsx";
import { Visits, type VisitOperationAccess } from "./visits.tsx";
import { clearOffline,offlinePending } from "./offline-store.ts";
import type { VisitsAccess } from "../shared/visits.ts";
import { Demands } from "./demands.tsx";
import { Calendar } from "./calendar.tsx";
import { MobileSettingsPanel } from "./mobile-settings.tsx";
import { Home } from "./home.tsx";
import { CompletePreview } from "./complete-preview.tsx";
import { Rankings, MyProfile } from "./progress.tsx";
import {Reports} from "./reports.tsx";
import { Goals } from "./goals.tsx";
import type { DemandGrant } from "../shared/demands.ts";
import "./shell.css";
const mobileBrowser = () =>
  /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
function readContext() {
  const params = new URLSearchParams(location.search);
  const page =
    params.get("view") || (params.has("office") ? "offices" : "home");
  const office = params.get("office");
  const filter = params.get("filter") || "draft";
  return {
    page: [
      "offices",
      "home",
      "preview",
      "demands",
      "calendar",
      "references",
      "config",
      "users",
      "visits-overview",
      "visits-sources",
      "visits",
      "maps",
      "reports",
      "rankings",
      "profile",
      "goals",
    ].includes(page)
      ? page
      : "offices",
    office: office && /^[a-f0-9-]{36}$/i.test(office) ? office : null,
    filter: [
      "draft",
      "in_progress",
      "awaiting_response",
      "closed",
      "all",
    ].includes(filter)
      ? filter
      : "draft",
    listPage: Math.max(1, Math.min(1000000, Number(params.get("p")) || 1)),
  };
}
function writeContext(
  page: string,
  office: string | null,
  filter: string,
  push = false,
  listPage = 1,
) {
  const params = new URLSearchParams();
  params.set("view", page);
  if (office && page === "offices") params.set("office", office);
  if (filter !== "draft") params.set("filter", filter);
  if (listPage > 1) params.set("p", String(listPage));
  const query = params.toString();
  const url = location.pathname + (query ? "?" + query : "");
  if (push) history.pushState({}, "", url);
  else history.replaceState({}, "", url);
}
function App() {
  const [user, setUser] = useState<User | null>(null),
    [loading, setLoading] = useState(true),
    [identity, setIdentity] = useState<PublicIdentity | null>(null),
    [config, setConfig] = useState<Config | null>(null),
    [page, setPage] = useState(() => readContext().page),
    [officeId, setOfficeId] = useState<string | null>(
      () => readContext().office,
    ),
    [listFilter, setListFilter] = useState(() => readContext().filter),
    [listQuery, setListQuery] = useState(""),
    [listPage, setListPage] = useState(() => readContext().listPage),
    [online, setOnline] = useState(navigator.onLine),
    [toast, setToast] = useState(""),
    [error, setError] = useState(""),
    [integrations, setIntegrations] = useState<Integration[]>([]),
    [notifications, setNotifications] = useState<Notice[]>([]),
    [showNotices, setShowNotices] = useState(false),
    [collapsed, setCollapsed] = useState(false),
    [refresh, setRefresh] = useState(0),
    [dirty, setDirty] = useState(false);
  const [visitsAccess, setVisitsAccess] = useState<VisitsAccess | null>(null);
  const [visitOperations,setVisitOperations]=useState<VisitOperationAccess|null>(null);
  useEffect(()=>{if(!user){setVisitOperations(null);return;}const c=new AbortController();api<VisitOperationAccess>('/visits/access','GET',undefined,c.signal).then(setVisitOperations).catch(()=>{if(!c.signal.aborted)setVisitOperations(null);});return()=>c.abort();},[user?.id,refresh]);
  const [previewLabel, setPreviewLabel] = useState<string | null>(null);
  const [completePreviewEnabled, setCompletePreviewEnabled] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const [officeDataOwner, setOfficeDataOwner] = useState<string | null>(null);
  const [demandGrant, setDemandGrant] = useState<DemandGrant | null>(null);
  const [calendarAllowed, setCalendarAllowed] = useState(false);
  const [mobileSettings, setMobileSettings] = useState(false);
  const [generalNotices, setGeneralNotices] = useState<
    (Notice & { source: string })[]
  >([]);
  const visitsPage = page === "visits-overview" || page === "visits-sources" || page === "maps" || page === "visits";
  const generalPage =
    visitsPage ||
    [
      "home",
      "demands",
      "calendar",
      "preview",
      "reports",
      "rankings",
      "profile",
      "goals",
    ].includes(page);
  const officeAllowed = user?.admin || user?.permissions.includes("access");
  const visibleNotifications = generalPage
    ? generalNotices
    : officeDataOwner === user?.id && officeAllowed
      ? notifications
      : [];
  function clearSessionData() {
    setUser(null);
    setAccountMenu(false);
    setCSRF("");
    setConfig(null);
    setIntegrations([]);
    setNotifications([]);
    setOfficeDataOwner(null);
    setShowNotices(false);
    setVisitsAccess(null);
    setVisitOperations(null);
    setDemandGrant(null);
    setCalendarAllowed(false);
    setGeneralNotices([]);
    setDirty(false);
    setError("");
    setToast("");
    setOfficeId(null);
    setListQuery("");
    listScroll.current = 0;
  }
  async function logout(){
    if(user){
      const pending=await offlinePending(user.id).catch(()=>[]);
      if(pending.length&&!window.confirm(`Há ${pending.length} pendências offline. Sair removerá essas pendências deste aparelho sem enviá-las. Deseja sair?`))throw Error("Saída cancelada. Envie suas pendências antes de sair.");
    }
    await api("/logout","POST");
    if(user)await clearOffline(user.id).catch(()=>{});
  }
  const dirtyContext = useRef(false),
    listScroll = useRef(0);
  dirtyContext.current = dirty;
  const notify = (s: string) => {
    setToast(s);
    setTimeout(() => setToast(""), 6500);
  };
  const failed = (e: any) => {
    const message = e.message || "A operação não foi concluída.";
    setError(message);
    window.dispatchEvent(
      new CustomEvent("operation-error", { detail: message }),
    );
  };
  useEffect(() => {
    api("/public-config")
      .then((c) => {
        setIdentity(c.identity);
        setPreviewLabel(c.previewLabel ?? null);
        setCompletePreviewEnabled(c.completePreviewEnabled === true);
      })
      .catch(() => {});
    api("/session")
      .then((s) => {
        setCSRF(s.csrf);
        setUser(s.user);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    const on = () => setOnline(true),
      off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    api<any>("/demands/access", "GET", undefined, controller.signal)
      .then((v) => setDemandGrant(v.grant))
      .catch(() => {});
    api<any[]>("/calendar/access", "GET", undefined, controller.signal)
      .then((v) => setCalendarAllowed(v.length > 0))
      .catch(() => {});
    setVisitsAccess(null);
    api<VisitsAccess>(
      "/visits-overview/access",
      "GET",
      undefined,
      controller.signal,
    )
      .then(setVisitsAccess)
      .catch(() => {});
    return () => controller.abort();
  }, [user, refresh]);
  useEffect(() => {
    if (!user || !generalPage) return;
    const controller = new AbortController();
    Promise.allSettled(
      ["demands", "calendar", "progress"].map(async (source) => {
        const rows = await api<Notice[]>(
          `/${source}/notices`,
          "GET",
          undefined,
          controller.signal,
        );
        return rows.map((n) => ({ ...n, source }));
      }),
    ).then((results) => {
      if (!controller.signal.aborted)
        setGeneralNotices(
          results.flatMap((r) => (r.status === "fulfilled" ? r.value : [])),
        );
    });
    return () => controller.abort();
  }, [user, generalPage, page, refresh]);
  useEffect(() => {
    setConfig(null);
    setIntegrations([]);
    setNotifications([]);
    setOfficeDataOwner(null);
    setShowNotices(false);
    if (!user || mobileBrowser() || generalPage || !officeAllowed) return;
    const controller = new AbortController();
    const active = () => !controller.signal.aborted;
    const handleFailure = (error: any) => {
      if (active()) failed(error);
    };
    api("/config", "GET", undefined, controller.signal)
      .then((c) => {
        if (!active()) return;
        setConfig(c.published.config);
        setIdentity(c.published.config.identity);
      })
      .catch(handleFailure);
    api("/integrations", "GET", undefined, controller.signal)
      .then((value) => {
        if (active()) setIntegrations(value);
      })
      .catch(handleFailure);
    api("/notifications", "GET", undefined, controller.signal)
      .then((value) => {
        if (active()) {
          setNotifications(value);
          setOfficeDataOwner(user.id);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [user, refresh, generalPage, officeAllowed]);
  useEffect(() => {
    const theme = identity?.theme || "light";
    const source = (tokens.themes as any)[theme];
    const groups = [
      source,
      tokens.typography,
      tokens.spacing,
      tokens.radii,
      tokens.shadows,
      tokens.motion,
      tokens.layout,
      tokens.fixed,
      identity?.colors || {},
    ];
    for (const group of groups)
      for (const [key, value] of Object.entries(group))
        if (typeof value === "string")
          document.documentElement.style.setProperty("--" + key, value);
    document.documentElement.dataset.theme = theme;
  }, [identity]);
  useEffect(() => {
    const before = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [dirty]);
  useEffect(() => {
    const pop = () => {
      if (
        dirtyContext.current &&
        !window.confirm(
          "Há alterações ainda não salvas. Sair desta tela e descartá-las?",
        )
      ) {
        writeContext(page, officeId, listFilter, true, listPage);
        return;
      }
      const context = readContext();
      setDirty(false);
      setPage(context.page);
      setOfficeId(context.office);
      setListFilter(context.filter);
      setListPage(context.listPage);
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [page, officeId, listFilter, listPage]);
  useEffect(() => {
    writeContext(page, officeId, listFilter, false, listPage);
  }, [page, officeId, listFilter, listPage]);
  useEffect(() => {
    if (user && !user.admin && page !== "offices" && !generalPage) {
      setPage("offices");
      setOfficeId(null);
    }
  }, [user, page, generalPage]);
  useEffect(() => {
    if (
      visitsAccess?.permissions.access &&
      page === "offices" &&
      (!officeAllowed || mobileBrowser())
    ) {
      setPage("visits-overview");
      setOfficeId(null);
    }
  }, [visitsAccess, page, officeAllowed]);
  function go(next: string, id: string | null = null) {
    if (
      dirty &&
      !window.confirm(
        "Há alterações ainda não salvas no servidor. Deseja sair desta tela e descartá-las?",
      )
    )
      return;
    if (page === "offices" && !officeId) listScroll.current = window.scrollY;
    writeContext(next, id, listFilter, true, listPage);
    if (id) requestAnimationFrame(() => window.scrollTo({ top: 0 }));
    setDirty(false);
    setOfficeId(id);
    setPage(next);
    setError("");
  }
  if (loading)
    return (
      <main className="boot">
        <span className="spinner" />
        <p>Verificando acesso…</p>
      </main>
    );
  if (!user)
    return (
      <Login
        identity={identity}
        online={online}
        onLogin={(s) => {
          setCSRF(s.csrf);
          setUser(s.user);
        }}
      />
    );
  if (page === "preview" && completePreviewEnabled)
    return (
      <CompletePreview
        go={go}
        officeAllowed={!!officeAllowed}
        visitsAllowed={!!visitsAccess?.permissions.access}
        demandAllowed={!!demandGrant?.access}
        calendarAllowed={calendarAllowed}
      />
    );
  if (!officeAllowed && !generalPage)
    return (
      <main className="access-gate">
        <Icon name="alert" size={32} />
        <h1>Acesso a ofícios não autorizado</h1>
        <p>Solicite ao administrador a permissão de acesso ao módulo.</p>
        <Button
          onClick={() =>
            logout().then(clearSessionData).catch(failed)
          }
        >
          Sair
        </Button>
      </main>
    );
  return (
    <>
      {!generalPage && (
        <div className="mobile-gate">
          <Icon name="desktop" size={40} />
          <h1>Ofícios no computador</h1>
          <p>
            Este módulo está disponível na experiência de computador. Abra o
            Gabinete Central em um computador autorizado para redigir e
            acompanhar seus ofícios.
          </p>
          <Button onClick={() => go("visits-overview")}>
            Visão geral das visitas
          </Button>
          <Button
            onClick={() =>
              logout().then(clearSessionData).catch(failed)
            }
          >
            Sair
          </Button>
        </div>
      )}
      {previewLabel && (
        <div className="environment-notice" role="note">
          {previewLabel}
        </div>
      )}
      <div
        className={
          "app " +
          (generalPage ? "general-app " : "") +
          (collapsed ? "collapsed" : "")
        }
      >
        <a className="skip" href="#main">
          Pular para o conteúdo
        </a>
        <aside className="sidebar">
          <div className="brand">
            {identity?.logoFileId ? (
              <img alt="Logo do gabinete" src="/api/logo" />
            ) : (
              <span className="brand-mark">
                <Icon name={generalPage ? "building" : "document"} size={25} />
              </span>
            )}
            {!collapsed && (
              <div>
                <strong>{identity?.name || "Gabinete Central"}</strong>
                <small>
                  {generalPage
                    ? "ORGANIZAR PARA ATENDER"
                    : "Gestão do gabinete"}
                </small>
              </div>
            )}
          </div>
          <button
            className="collapse"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expandir navegação" : "Recolher navegação"}
          >
            <Icon name={collapsed ? "arrow" : "back"} size={16} />
          </button>
          <div className="nav-label">
            {generalPage ? "SEU TRABALHO" : "TRABALHO"}
          </div>
          <nav aria-label="Navegação principal">
            {completePreviewEnabled && (
              <Nav
                active={page === "preview"}
                label="Prévia completa"
                icon="library"
                collapsed={collapsed}
                onClick={() => go("preview")}
              />
            )}
            <Nav
              active={page === "home"}
              label="Início"
              icon="home"
              collapsed={collapsed}
              onClick={() => go("home")}
            />
            {demandGrant?.access && (
              <Nav
                active={page === "demands"}
                label="Demandas"
                icon="document"
                collapsed={collapsed}
                onClick={() => go("demands")}
              />
            )}
            {calendarAllowed && (
              <Nav
                active={page === "calendar"}
                label="Calendário"
                icon="calendar"
                collapsed={collapsed}
                onClick={() => go("calendar")}
              />
            )}
            {officeAllowed && (
              <Nav
                active={page === "offices"}
                label="Ofícios"
                icon="document"
                collapsed={collapsed}
                onClick={() => go("offices")}
              />
            )}
            {!!visitOperations?.sources.length && <Nav active={page === 'visits'} label="Visitas" icon="visits" collapsed={collapsed} onClick={()=>go('visits')}/>}
            {visitsAccess?.permissions.access && (
              <Nav
                active={page === "visits-overview"}
                label="Visão geral das visitas"
                icon="search"
                collapsed={collapsed}
                onClick={() => go("visits-overview")}
              />
            )}
            {visitsAccess?.permissions.configure && (
              <Nav
                active={page === "visits-sources"}
                label="Fontes e acessos de visitas"
                icon="settings"
                collapsed={collapsed}
                onClick={() => go("visits-sources")}
              />
            )}
            {visitsAccess?.permissions.records && <Nav active={page === "maps"} label="Mapas" icon="visits" collapsed={collapsed} onClick={()=>go("maps")} />}
            <div className="nav-label">SEUS RESULTADOS</div><Nav active={page === "reports"} label="Relatórios" icon="chart" collapsed={collapsed} onClick={()=>go("reports")} />
            <Nav
              active={page === "goals"}
              label="Metas"
              icon="chart"
              collapsed={collapsed}
              onClick={() => go("goals")}
            />
            <Nav
              active={page === "rankings"}
              label="Rankings"
              icon="chart"
              collapsed={collapsed}
              onClick={() => go("rankings")}
            />
            <Nav
              active={page === "profile"}
              label="Meu perfil"
              icon="user"
              collapsed={collapsed}
              onClick={() => go("profile")}
            />
            {user.admin && (
              <>
                <div className="nav-label">ADMINISTRAÇÃO</div>
                <Nav
                  active={page === "references"}
                  label="Referências da IA"
                  icon="library"
                  collapsed={collapsed}
                  onClick={() => go("references")}
                />
                <Nav
                  active={page === "config"}
                  label="Configurações"
                  icon="settings"
                  collapsed={collapsed}
                  onClick={() => go("config")}
                />
                <Nav
                  active={page === "users"}
                  label="Pessoas e permissões"
                  icon="users"
                  collapsed={collapsed}
                  onClick={() => go("users")}
                />
              </>
            )}
          </nav>
          <div className="sidebar-bottom">
            <div className="avatar">{user.name.slice(0, 1).toUpperCase()}</div>
            {!collapsed && (
              <div>
                <strong>{user.name}</strong>
                <small>
                  {user.admin ? "Administrador" : "Equipe do gabinete"}
                </small>
              </div>
            )}
            <button
              title="Sair"
              aria-label="Sair"
              onClick={() => {
                if (
                  dirty &&
                  !window.confirm("Sair e descartar alterações não salvas?")
                )
                  return;
                logout().then(clearSessionData).catch(failed);
              }}
            >
              <Icon name="logout" />
            </button>
          </div>
        </aside>
        <div className="workspace">
          <header className="topbar">
            <div className="breadcrumb">
              {generalPage && (
                <b className="general-mobile-brand" title={identity?.name}>
                  {identity?.logoFileId ? (
                    <img
                      alt={identity.name || "Logo do gabinete"}
                      src="/api/logo"
                    />
                  ) : (
                    (
                      {
                        home: `Olá, ${user.name.split(" ")[0]}.`,
                        demands: "Demandas",
                        calendar: "Calendário",
                      } as any
                    )[page] ||
                    identity?.name ||
                    "Gabinete Central"
                  )}
                </b>
              )}
              <span className="desktop-breadcrumb">
                Gabinete Central <span>/</span>{" "}
                {page === "reports" ? "Relatórios" : page === "visits" ? "Visitas" : page === "maps" ? "Mapas" : page === "goals"
                  ? "Metas"
                  : page === "rankings"
                    ? "Rankings"
                    : page === "profile"
                      ? "Meu perfil"
                      : page === "home"
                        ? "Início"
                        : page === "demands"
                          ? "Demandas"
                          : page === "calendar"
                            ? "Calendário"
                            : page === "visits-overview"
                              ? "Visão geral das visitas"
                              : page === "visits-sources"
                                ? "Fontes e acessos de visitas"
                                : page === "references"
                                  ? "Biblioteca de referências"
                                  : page === "config"
                                    ? "Configurações"
                                    : page === "users"
                                      ? "Pessoas e permissões"
                                      : "Ofícios"}
                {officeId && (
                  <>
                    <span>/</span> Documento
                  </>
                )}
              </span>
            </div>
            <div className="top-actions">
              {generalPage && (
                <Button
                  kind="ghost"
                  className="general-account-button"
                  aria-label="Abrir conta e áreas autorizadas"
                  onClick={() => setAccountMenu(true)}
                >
                  <span className="account-initials" aria-hidden="true">
                    {user.name
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((n) => n[0])
                      .join("")}
                  </span>
                </Button>
              )}
              <span className={"connection " + (!online ? "warning" : "")}>
                <i />
                {online ? "Conectado" : "Sem internet"}
              </span>
              <button
                className="notification-button"
                aria-label={`Notificações, ${visibleNotifications.filter((n) => !n.read).length} não lidas`}
                aria-expanded={showNotices}
                onClick={() => setShowNotices(!showNotices)}
              >
                <Icon name="bell" />
                {visibleNotifications.some((n) => !n.read) && <span />}
              </button>
            </div>
            {showNotices && (
              <section className="notice-panel" aria-label="Notificações">
                <h2>Notificações</h2>
                {visibleNotifications.length ? (
                  visibleNotifications.map((n, i) => (
                    <article
                      key={n.id || i}
                      className={n.read ? "notice-read" : ""}
                    >
                      <strong>{n.title || "Atualização de ofício"}</strong>
                      <p>{n.text || n.message}</p>
                      {(n.createdAt || n.at) && (
                        <small>{date(n.createdAt || n.at)}</small>
                      )}
                      {n.id && !n.read && (
                        <Button
                          kind="ghost"
                          onClick={() =>
                            api(
                              ("source" in n
                                ? "/" + n.source + "/notices/"
                                : "/notifications/") +
                                n.id +
                                "/read",
                              "POST",
                            )
                              .then(() =>
                                (generalPage
                                  ? setGeneralNotices
                                  : setNotifications)((current: any) =>
                                  current.map((item: Notice) =>
                                    item.id === n.id
                                      ? { ...item, read: true }
                                      : item,
                                  ),
                                ),
                              )
                              .catch(failed)
                          }
                        >
                          Marcar como lida
                        </Button>
                      )}
                    </article>
                  ))
                ) : (
                  <p>Nenhuma notificação disponível.</p>
                )}
              </section>
            )}
          </header>
          <main id="main" tabIndex={-1}>
            {completePreviewEnabled && page === "home" && (
              <div className="complete-preview-entry">
                <Button
                  kind="primary"
                  icon="library"
                  onClick={() => go("preview")}
                >
                  Ver prévia completa · todos os módulos
                </Button>
                <span>
                  Visual aprovado com dados demonstrativos. Os módulos
                  funcionais continuam separados.
                </span>
              </div>
            )}
            {!online && !visitsPage && (
              <Banner kind="warn">
                Sem internet. As edições nesta tela ainda não salvas continuam
                apenas na memória desta página. Mantenha-a aberta e salve após
                reconectar.
              </Banner>
            )}
            {page === "home" && (
              <Home
                user={user}
                demandAccess={!!demandGrant?.access}
                visitsAccess={!!visitsAccess?.permissions.access}
                visitsIndicators={!!visitsAccess?.permissions.indicators}
                calendarAccess={calendarAllowed}
                go={go}
                onAcceptance={() => {
                  go("demands");
                  window.dispatchEvent(new Event("demand-acceptance-open"));
                }}
                onOpenDemand={(id) => {
                  go("demands");
                  window.dispatchEvent(
                    new CustomEvent("demand-record-open", { detail: id }),
                  );
                }}
              />
            )}
            {page === "visits" && <Visits user={user} notify={notify}/>}
            {page === "maps" && <Maps access={visitsAccess} admin={user.admin} />}
            {demandGrant?.access && (
              <div hidden={page !== "demands"}>
                <Demands
                  key={user.id}
                  user={user}
                  active={page === "demands"}
                  onDirty={setDirty}
                  notify={notify}
                />
              </div>
            )}
            {page === "demands" && demandGrant && !demandGrant.access && (
              <Banner kind="warn">
                Acesso a Demandas não autorizado. Solicite ao administrador.
              </Banner>
            )}
            {calendarAllowed && (
              <div hidden={page !== "calendar"}>
                <Calendar user={user} onDirty={setDirty} notify={notify} />
              </div>
            )}
            {page === "calendar" && !calendarAllowed && (
              <Banner>
                Seu acesso ao calendário ainda não foi habilitado pelo
                administrador.
              </Banner>
            )}
            {error && (
              <div className="error-slot">
                <Banner kind="danger">{error}</Banner>
                <Button
                  kind="ghost"
                  aria-label="Dispensar mensagem"
                  icon="close"
                  onClick={() => setError("")}
                />
              </div>
            )}
            {page === "offices" &&
              (officeId ? (
                <OfficeEditor
                  key={officeId}
                  id={officeId}
                  user={user}
                  config={config}
                  online={online}
                  onBack={() => go("offices")}
                  onOpen={(id) => go("offices", id)}
                  notify={notify}
                  failed={failed}
                  onDirty={setDirty}
                />
              ) : (
                <OfficeList
                  user={user}
                  config={config}
                  online={online}
                  integrations={integrations}
                  query={listQuery}
                  setQuery={(q) => {
                    setListQuery(q);
                    setListPage(1);
                  }}
                  filter={listFilter}
                  setFilter={(f) => {
                    setListFilter(f);
                    setListPage(1);
                  }}
                  initialScroll={listScroll.current}
                  listPage={listPage}
                  setListPage={setListPage}
                  onOpen={(id) => go("offices", id)}
                  failed={failed}
                />
              ))}
            {page === "references" && user.admin && (
              <Library
                online={online}
                config={config}
                failed={failed}
                notify={notify}
              />
            )}
            {page === "config" && user.admin && (
              <Configuration
                integrations={integrations}
                online={online}
                onDirty={setDirty}
                failed={failed}
                notify={notify}
                onPublished={() => setRefresh((n) => n + 1)}
              />
            )}
            {page === "users" && user.admin && (
              <Users online={online} failed={failed} notify={notify} />
            )}
            {page === "visits-overview" && (
              <VisitsOverview user={user} notify={notify} />
            )}
            {page === "reports" && <Reports />}
            {page === "rankings" && <Rankings user={user} notify={notify} />}
            {page === "goals" && <Goals user={user} notify={notify} />}
            {page === "profile" && <MyProfile user={user} notify={notify} />}
            {page === "visits-sources" && (
              <VisitsOverviewAdmin
                user={user}
                notify={notify}
                onPublished={() => setRefresh((value) => value + 1)}
                onDirty={setDirty}
              />
            )}
          </main>
          <footer className="app-footer">
            {page === "profile"
              ? "Meu perfil · Dados pessoais e progresso"
              : page === "reports" ? "Relatórios · Dados autorizados e critérios publicados"
              : page === "rankings"
                ? "Rankings · Recompensas efetivamente registradas"
                : page === "goals"
                  ? "Metas · Objetivos e contribuições autorizadas"
                  : page === "home"
                    ? "Gabinete Central · Área de trabalho"
                    : page === "demands"
                      ? "Demandas · Registros protegidos por acesso"
                      : page === "calendar"
                        ? "Calendário · Agendas autorizadas"
                        : generalPage
                          ? "Visitas · Consulta consolidada conforme suas permissões"
                          : "Ofícios · Redação assistida e protocolo com confirmação"}
          </footer>
        </div>
      </div>
      {generalPage && (
        <nav className="system-bottom-nav" aria-label="Navegação móvel">
          <button
            aria-current={page === "home" ? "page" : undefined}
            onClick={() => go("home")}
          >
            <Icon name="home" />
            Início
          </button>
          {demandGrant?.access && (
            <button
              aria-current={page === "demands" ? "page" : undefined}
              onClick={() => go("demands")}
            >
              <Icon name="document" />
              Demandas
            </button>
          )}
          {(visitsAccess?.permissions.access || !!visitOperations?.sources.length) && (
            <button
              aria-current={visitsPage ? "page" : undefined}
              onClick={() => go(visitOperations?.sources.length ? "visits" : "visits-overview")}
            >
              <Icon name="search" />
              Visitas
            </button>
          )}
          {calendarAllowed && (
            <button
              aria-current={page === "calendar" ? "page" : undefined}
              onClick={() => go("calendar")}
            >
              <Icon name="calendar" />
              Calendário
            </button>
          )}
          <button
            aria-expanded={accountMenu}
            onClick={() => setAccountMenu(true)}
          >
            <Icon name="settings" />
            Mais
          </button>
        </nav>
      )}
      {mobileSettings && user.admin && (
        <MobileSettingsPanel onClose={() => setMobileSettings(false)} />
      )}
      {accountMenu && generalPage && (
        <Modal title="Conta e áreas" onClose={() => setAccountMenu(false)}>
          <div className="general-account-menu">
            {completePreviewEnabled && (
              <Button
                onClick={() => {
                  setAccountMenu(false);
                  go("preview");
                }}
              >
                Prévia completa · todos os módulos
              </Button>
            )}
            <Button onClick={()=>{setAccountMenu(false);go("reports");}}>Relatórios</Button>
            <p>{identity?.name || "Gabinete Central"}</p>
            <strong>{user.name}</strong>
            {!!visitOperations?.sources.length && <Button onClick={()=>{setAccountMenu(false);go('visits');}}>Visitas</Button>}
            {visitsAccess?.permissions.records && <Button onClick={()=>{setAccountMenu(false);go("maps");}}>Mapas</Button>}
            <Button
              onClick={() => {
                setAccountMenu(false);
                go("goals");
              }}
            >
              Metas
            </Button>
            <Button
              onClick={() => {
                setAccountMenu(false);
                go("profile");
              }}
            >
              Meu perfil
            </Button>
            <Button
              onClick={() => {
                setAccountMenu(false);
                go("rankings");
              }}
            >
              Rankings
            </Button>
            <Button
              onClick={() => {
                setAccountMenu(false);
                go("home");
              }}
            >
              Início
            </Button>
            {demandGrant?.access && (
              <Button
                onClick={() => {
                  setAccountMenu(false);
                  go("demands");
                }}
              >
                Demandas
              </Button>
            )}
            {calendarAllowed && (
              <Button
                onClick={() => {
                  setAccountMenu(false);
                  go("calendar");
                }}
              >
                Calendário
              </Button>
            )}
            {officeAllowed && !mobileBrowser() && (
              <Button
                onClick={() => {
                  setAccountMenu(false);
                  go("offices");
                }}
              >
                Ofícios
              </Button>
            )}
            {user.admin && (
              <Button
                onClick={() => {
                  setAccountMenu(false);
                  setMobileSettings(true);
                }}
              >
                Aplicativo e atualizações
              </Button>
            )}
            {visitsAccess?.permissions.access && (
              <Button
                icon="chart"
                onClick={() => {
                  setAccountMenu(false);
                  go("visits-overview");
                }}
              >
                Visão geral das visitas
              </Button>
            )}
            {visitsAccess?.permissions.configure && (
              <Button
                icon="settings"
                onClick={() => {
                  setAccountMenu(false);
                  go("visits-sources");
                }}
              >
                Fontes e acessos
              </Button>
            )}
            <Button
              kind="ghost"
              icon="logout"
              onClick={() =>
                logout()
                  .then(() => {
                    setAccountMenu(false);
                    clearSessionData();
                  })
                  .catch(failed)
              }
            >
              Sair
            </Button>
          </div>
        </Modal>
      )}
      {toast && (
        <div className="toast" role="status">
          <Icon name="check" />
          {toast}
        </div>
      )}
    </>
  );
}
function Nav({
  active,
  label,
  icon,
  collapsed,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: string;
  collapsed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={"nav-item " + (active ? "active" : "")}
      title={collapsed ? label : undefined}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <Icon name={icon} />
      {!collapsed && label}
    </button>
  );
}
function Login({
  identity,
  online,
  onLogin,
}: {
  identity: PublicIdentity | null;
  online: boolean;
  onLogin: (s: any) => void;
}) {
  const [phone, setPhone] = useState(""),
    [password, setPassword] = useState(""),
    [show, setShow] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <main className="login">
      <form
        className="login-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            onLogin(await api("/login", "POST", { phone, password }));
          } catch (e: any) {
            setError(e.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {identity?.logoFileId && (
          <img className="login-logo" alt="Logo do gabinete" src="/api/logo" />
        )}
        <h1>Acesse seu gabinete</h1>
        <p>Entre com seu telefone e senha.</p>
        {new URLSearchParams(window.location.search).get("access_updated") ===
          "1" && (
          <Banner>
            Alteração salva. Entre novamente para continuar com os dados de
            acesso atualizados.
          </Banner>
        )}
        {error && <Banner kind="danger">{error}</Banner>}
        {!online && (
          <Banner kind="warn">Sem conexão. Conecte-se para entrar.</Banner>
        )}
        <Field label="Telefone">
          <input
            type="tel"
            autoComplete="username"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
        </Field>
        <Field label="Senha">
          <div className="password-field">
            <input
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              aria-label={show ? "Ocultar senha" : "Mostrar senha"}
              onClick={() => setShow(!show)}
            >
              {show ? "Ocultar" : "Mostrar"}
            </button>
          </div>
        </Field>
        <Button type="submit" kind="primary" busy={busy} disabled={!online}>
          Entrar
        </Button>
        <p className="login-help">
          Esqueceu a senha? Solicite a recuperação de acesso ao administrador.
        </p>
      </form>
      <small className="login-footer">
        Acesso restrito à equipe autorizada.
      </small>
    </main>
  );
}
createRoot(document.getElementById("root")!).render(<App />);


